/**
 * Centralized API Configuration Utility
 * Handles API URL configuration for both development and production environments
 */

// Get the API URL from environment variable (set in production) or use empty string (for development proxy)
const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Get the full API URL for an endpoint
 * @param endpoint - The API endpoint (e.g., '/api/feasibility')
 * @returns The full URL for the endpoint
 */
export const getApiUrl = (endpoint: string): string => {
  // In production, use the full backend URL
  if (API_URL) {
    return `${API_URL}${endpoint}`;
  }
  // In development, use relative URL (will be proxied by webpack dev server)
  return endpoint;
};

/**
 * Helper to build URL with query parameters
 * @param endpoint - The API endpoint
 * @param params - Query parameters as an object
 * @returns The full URL with query parameters
 */
export const getApiUrlWithParams = (endpoint: string, params: Record<string, any>): string => {
  const url = getApiUrl(endpoint);
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  
  const queryString = queryParams.toString();
  return queryString ? `${url}?${queryString}` : url;
};

/**
 * Common fetch configuration with auth headers
 * @returns Fetch request configuration
 */
export const getFetchConfig = (options: RequestInit = {}): RequestInit => {
  const token = localStorage.getItem('access_token');
  
  return {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };
};

// Export the API URL for components that need it directly
export const API_BASE_URL = API_URL;